import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useFacilityProfile } from "@src/appV2/Facilities/api/useFacilityProfile";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import constate from "constate";
import { z } from "zod";

const chartingSoftwareEnum = z.enum([
  "PointClickCare",
  "Matrix",
  "NetSmart",
  "American Health Tech",
  "Other",
]);

const chartingCredentialSchema = z.object({
  software: chartingSoftwareEnum.optional(),
  username: z.string().optional(),
  password: z.string().optional(),
});

const chartingCredentialsAttributesSchema = z.object({
  workerId: z.string(),
  workplaceId: z.string(),
  chartingCredentials: chartingCredentialSchema.optional(),
  timeclockCredentials: z
    .object({
      pin: z.string(),
    })
    .optional(),
});

const chartingCredentialsDataSchema = z.object({
  type: z.literal("worker-credential"),
  id: z.string(),
  attributes: chartingCredentialsAttributesSchema,
});

const getChartingCredentialsResponseSchema = z.object({
  data: z.array(chartingCredentialsDataSchema),
});

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const chartingContextSchema = z.object({
  chartingCredential: chartingCredentialSchema.optional(),
  chartingCredentialSharingIsEnabled: z.boolean(),
});

export type ChartingCredentialsAttributes = z.infer<typeof chartingCredentialsAttributesSchema>;
export type ChartingCredentialsData = z.infer<typeof chartingCredentialsDataSchema>;
export type GetChartingCredentialsResponse = z.infer<typeof getChartingCredentialsResponseSchema>;
export type ChartingContextData = z.infer<typeof chartingContextSchema>;
export type ChartingCredential = z.infer<typeof chartingCredentialSchema>;

export function useGetChartingCredentialSharingIsEnabled(workplaceId: string | undefined) {
  const {
    data: facilityProfile,
    isLoading,
    isError,
  } = useFacilityProfile(
    { workplaceId: workplaceId ?? "", projection: "chartingCredentialSharingIsEnabled" },
    { enabled: isDefined(workplaceId) }
  );

  const chartingCredentialSharingIsEnabled =
    isDefined(workplaceId) &&
    !isLoading &&
    !isError &&
    isDefined(facilityProfile?.chartingCredentialSharingIsEnabled) &&
    facilityProfile.chartingCredentialSharingIsEnabled;
  return {
    chartingCredentialSharingIsEnabled,
    isLoading,
    isError,
  };
}

export function useGetChartingCredentials(
  workerId: string,
  workplaceId: string,
  options: UseGetQueryOptions<GetChartingCredentialsResponse> = {}
) {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/worker-credentials`,
    queryParams: {
      filter: {
        workerId,
        workplaceId,
      },
    },
    responseSchema: getChartingCredentialsResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_CREDENTIALS_FAILURE,
    },
    ...options,
  });
}

function useChartingContext(props: { workerId: string; workplaceId: string | undefined }) {
  const { workerId, workplaceId } = props;

  const chartingReminderModalState = useModalState();
  const chartingCredentialsModalState = useModalState();

  const {
    chartingCredentialSharingIsEnabled,
    isLoading: isWorkplaceProfileLoading,
    isError: isWorkplaceProfileError,
  } = useGetChartingCredentialSharingIsEnabled(workplaceId);

  const {
    data: chartingCredentialsResponse,
    isLoading: isChartingCredentialsLoading,
    isError: isChartingCredentialsError,
  } = useGetChartingCredentials(workerId, workplaceId ?? "", {
    enabled: chartingCredentialSharingIsEnabled,
  });

  const firstChartingCredential =
    isChartingCredentialsLoading ||
    isChartingCredentialsError ||
    !isDefined(chartingCredentialsResponse?.data) ||
    !Array.isArray(chartingCredentialsResponse.data)
      ? undefined
      : chartingCredentialsResponse.data[0]?.attributes?.chartingCredentials;

  const contextData: ChartingContextData = {
    chartingCredential: firstChartingCredential,
    chartingCredentialSharingIsEnabled,
  };

  return {
    data: contextData,
    isLoading: isWorkplaceProfileLoading || isChartingCredentialsLoading,
    isError: isWorkplaceProfileError || isChartingCredentialsError,
    // Modal states
    chartingReminderModalState,
    chartingCredentialsModalState,
  };
}

// Export the context provider and hook
export const [ChartingContextProvider, useChartingContextData] = constate(useChartingContext);
